class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();

      if (cart && window.updateShippingBar) {
        window.updateShippingBar(cart);
      }

      const { CartViewEvent } = window.StandardEvents || {};
      if (!CartViewEvent || !cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }



  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHtml) return;

      const innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (innerHTML == null) return;

      sectionElement.innerHTML = innerHTML;
    });

    // Always refresh header cart count from section HTML when present
    if (parsedState.sections?.['cart-icon-bubble']) {
      window.updateHeaderCartIcon?.(parsedState.sections['cart-icon-bubble']);
    }

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);

/**
 * Replace the header cart icon contents with freshly rendered section HTML.
 * Used after add-to-cart so the count updates instantly.
 */
window.updateHeaderCartIcon = function updateHeaderCartIcon(sectionHtml) {
  const cartIcon = document.getElementById('cart-icon-bubble');
  if (!cartIcon || !sectionHtml) return false;

  const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const source = doc.querySelector('.shopify-section') || doc.body;
  if (!source) return false;

  cartIcon.innerHTML = source.innerHTML;
  return true;
};

window.refreshHeaderCartIcon = function refreshHeaderCartIcon() {
  return fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-icon-bubble`)
    .then((response) => response.json())
    .then((sections) => {
      if (sections?.['cart-icon-bubble']) {
        window.updateHeaderCartIcon(sections['cart-icon-bubble']);
      }
    })
    .catch(() =>
      fetch(`${routes.cart_url}.js`)
        .then((response) => response.json())
        .then((cart) => {
          const cartIcon = document.getElementById('cart-icon-bubble');
          if (!cartIcon || !cart) return;

          let bubble = cartIcon.querySelector('.cart-count-bubble');
          if (cart.item_count > 0) {
            if (!bubble) {
              bubble = document.createElement('div');
              bubble.className = 'cart-count-bubble';
              cartIcon.appendChild(bubble);
            }
            const countLabel =
              cart.item_count === 1 ? '1 item' : `${cart.item_count} items`;
            bubble.innerHTML =
              cart.item_count < 100
                ? `<span aria-hidden="true">${cart.item_count}</span><span class="visually-hidden">${countLabel}</span>`
                : `<span class="visually-hidden">${countLabel}</span>`;
          } else if (bubble) {
            bubble.remove();
          }
        })
    );
};

